/**
 * The other people on the graph: their pointer with their name, a ring in their colour around
 * the node or group they selected, and for someone in 3D the ray they point along.
 *
 * A cursor is attached to a node (`state/cursors.ts`), so it is drawn by this page's own copy
 * of that node: right on the thing it points at, whatever this page's layout, zoom, 2D/3D view
 * or camera. Cursors arrive about 20 times a second, so the one shown glides towards the last
 * (`glide`, in world coordinates: panning or zooming here still moves it at once).
 *
 * Someone in 3D points along a line of sight: it is drawn as a ray ending at their cursor
 * (dashed, solid once it hits a node, which then gets a dashed ring), their name carries a "3D"
 * tag, and for people in 2D, whose picture differs, the cursor is see-through.
 *
 * The scene is not reactive (it moves every frame), so the elements are made by Solid but
 * placed by an animation-frame loop.
 */
import { For, Show, onCleanup, onMount, type JSX } from "solid-js";
import type { Person } from "@protocol/Person";
import { useApp } from "../../app/context";
import { cursorPoint, pointedNode, rayStart } from "../../state/cursors";
import { glide, type WorldPoint } from "../../state/presence";

/** Where one person's elements are, to place them each frame. */
interface Placed {
  /** Their pointer and name. */
  cursor: HTMLDivElement;
  /** The ring around what they selected. */
  ring: HTMLDivElement;
  /** The dashed ring around the node their ray hits. */
  target: HTMLDivElement;
  /** The ray they point along (3D). */
  ray: SVGLineElement;
  /** Where their cursor is shown now (`null` while hidden: it then appears where it is). */
  shown: WorldPoint | null;
}

/** Draws everyone else's cursor, ray and selection over the graph. */
export function PeopleLayer(): JSX.Element {
  const { presence, scene } = useApp();
  const placed = new Map<string, Placed>();

  /** Puts one person's elements where they are on this screen, `dt` ms after the last frame. */
  const place = (person: Person, el: Placed, dt: number) => {
    const cursor = presence.cursors()[person.id];
    if (!cursor) {
      el.shown = null;
      el.cursor.hidden = true;
      el.ray.style.display = "none";
      el.target.hidden = true;
    } else {
      const target = cursorPoint(scene, cursor);
      el.shown = el.shown ? glide(el.shown, target, dt) : target;
      const at = scene.toScreen(el.shown);
      el.cursor.style.transform = `translate(${at.x}px, ${at.y}px)`;
      // Their view differs from ours: the point is right, but on a picture we do not see.
      el.cursor.dataset.otherView = cursor.in_3d !== (scene.dimension === "3d") ? "yes" : "no";
      el.cursor.hidden = false;
      const start = rayStart(cursor, el.shown);
      if (start) {
        const from = scene.toScreen(start);
        el.ray.setAttribute("x1", `${from.x}`);
        el.ray.setAttribute("y1", `${from.y}`);
        el.ray.setAttribute("x2", `${at.x}`);
        el.ray.setAttribute("y2", `${at.y}`);
        el.ray.dataset.hit = cursor.anchor?.on ? "yes" : "no";
        el.ray.style.display = "";
      } else {
        el.ray.style.display = "none";
      }
      const pointed = pointedNode(scene, cursor);
      placeRing(el.target, pointed ? nodeRing(pointed, 9) : null);
    }
    placeRing(el.ring, ringOf(person));
  };

  /** Shows a ring at a screen circle, or hides it. */
  const placeRing = (ring: HTMLDivElement, circle: { x: number; y: number; r: number } | null) => {
    if (!circle) {
      ring.hidden = true;
      return;
    }
    ring.style.transform = `translate(${circle.x - circle.r}px, ${circle.y - circle.r}px)`;
    ring.style.width = ring.style.height = `${circle.r * 2}px`;
    ring.hidden = false;
  };

  /** The screen circle `gap` pixels around a node. */
  const nodeRing = (n: Parameters<typeof scene.rs>[0], gap: number) => {
    const at = scene.toScreen(n);
    return { x: at.x, y: at.y, r: scene.rs(n) + gap };
  };

  /** The circle around what `person` selected, on screen (nodes and groups only). */
  const ringOf = (person: Person): { x: number; y: number; r: number } | null => {
    const s = person.selection;
    if (s?.kind === "node") {
      const n = scene.byId.get(s.id);
      return n?.visible ? nodeRing(n, 6) : null;
    }
    if (s?.kind === "group" && scene.showGroups) {
      const g = scene.groupById.get(s.id);
      if (!g?.visible) return null;
      const at = scene.toScreen(g);
      return { x: at.x, y: at.y, r: g.r * scene.view.k * at.scale + 4 };
    }
    return null;
  };

  onMount(() => {
    let frame = 0;
    let last = performance.now();
    const loop = (now: number) => {
      // A long pause (hidden tab) jumps straight to the end instead of gliding from far away.
      const dt = Math.min(now - last, 1000);
      last = now;
      for (const person of presence.others()) {
        const el = placed.get(person.id);
        if (el) place(person, el, dt);
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    onCleanup(() => cancelAnimationFrame(frame));
  });

  return (
    <div class="people-layer" aria-hidden="true">
      <For each={presence.others()}>
        {(person) => {
          const el = { shown: null } as Placed;
          onMount(() => placed.set(person.id, el));
          onCleanup(() => placed.delete(person.id));
          return (
            <>
              <svg class="person-rays">
                <line data-person-ray={person.id} ref={(r) => (el.ray = r)} stroke={person.colour} style={{ display: "none" }} />
              </svg>
              <div class="person-ring" data-person-ring={person.id} ref={(r) => (el.ring = r)} style={{ "border-color": person.colour }} hidden />
              <div class="person-target" data-person-target={person.id} ref={(r) => (el.target = r)} style={{ "border-color": person.colour }} hidden />
              <div class="person-cursor" data-person-cursor={person.id} ref={(r) => (el.cursor = r)} hidden>
                <svg viewBox="0 0 16 16" width="16" height="16">
                  <path d="M1 1l5.5 13 2-5.5L14 6.5z" fill={person.colour} stroke="var(--panel)" stroke-width="1.2" stroke-linejoin="round" />
                </svg>
                <span style={{ background: person.colour }}>
                  {person.name}
                  <Show when={presence.cursors()[person.id]?.in_3d}>
                    <b class="tag-3d" data-person-3d={person.id} title={`${person.name} is in the 3D view`}>
                      3D
                    </b>
                  </Show>
                </span>
              </div>
            </>
          );
        }}
      </For>
    </div>
  );
}
